// src/lib/upload/utils/track-mapper.ts

import { Draft } from "@/types/draft";
import { supabase } from "@/lib/supabase";

/**
 * Maps a raw Supabase track object (from the database) to the local
 * application's `Draft` interface. This function is now schema-aware.
 *
 * @param supabaseTrackData The data object from the Supabase `tracks` table.
 * @param profilesMap Optional map of userId -> profile data for batch fetching.
 * @returns A `Draft` object that conforms to the application's `Draft` interface.
 */
export const mapSupabaseTrackToDraft = async (
  supabaseTrackData: any,
  profilesMap?: Map<string, any>
): Promise<Draft> => {
  // Never fabricate a credit. An empty string means "no credit on file", which
  // isMissingCredit() recognises and formatCredit() renders per organisation — "Add composer"
  // for a production library, "Unknown artist" only where that is genuinely the right noun.
  // Inventing a name here made 13,978 creditless tracks look attributed.
  const artistName = supabaseTrackData.artist || "";

  // Extract uploader name from joined profiles data
  let uploaderName: string | undefined;
  if (supabaseTrackData.profiles) {
    // Handle both array and object formats from Supabase join
    if (Array.isArray(supabaseTrackData.profiles) && supabaseTrackData.profiles.length > 0) {
      uploaderName = supabaseTrackData.profiles[0]?.display_name;
    } else if (typeof supabaseTrackData.profiles === 'object') {
      uploaderName = supabaseTrackData.profiles.display_name;
    }
  }

  // Fallback: Use profilesMap if provided, otherwise fetch separately
  if (!uploaderName && supabaseTrackData.user_id) {
    if (profilesMap && profilesMap.has(supabaseTrackData.user_id)) {
      const profile = profilesMap.get(supabaseTrackData.user_id);
      uploaderName = profile?.display_name ?? undefined;
    } else {
      // Only fetch individually if profilesMap not provided
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name")
          .eq("user_id", supabaseTrackData.user_id)
          .single();
        uploaderName = profile?.display_name ?? undefined;
      } catch (error) {
        // Silently fail - uploader name is optional
        console.debug("Could not fetch uploader name:", error);
      }
    }
  }

  const metadata = {
    title: supabaseTrackData.title,
    duration: supabaseTrackData.duration,
    bpm: supabaseTrackData.bpm,
    key: supabaseTrackData.key,
    genre: supabaseTrackData.genre,
    subgenre: supabaseTrackData.subgenre,
    moods: supabaseTrackData.moods,
    instruments: supabaseTrackData.instruments,
    vocal_gender: supabaseTrackData.vocal_gender,
    vocal_type: supabaseTrackData.vocal_type,
    explicit_content: supabaseTrackData.explicit_content,
    description: supabaseTrackData.description,
    emotional_arc: supabaseTrackData.emotional_arc,
    language: supabaseTrackData.language,
    harmony: supabaseTrackData.harmony,
    chord_progression: supabaseTrackData.chord_progression,
    lyrical_theme: supabaseTrackData.lyrical_theme,
    cultural_fusion: supabaseTrackData.cultural_fusion,
    historical_period: supabaseTrackData.historical_period,
    lyrics: supabaseTrackData.lyrics, // From the new top-level column
    // ACRCloud metadata
    album: supabaseTrackData.album,
    isrc: supabaseTrackData.isrc,
    upc: supabaseTrackData.upc,
    release_date: supabaseTrackData.release_date,
  };

  // For cover art, keep the original URL as it's usually public
  let signedCoverArtUrl =
    supabaseTrackData.cover_art_url || supabaseTrackData.artwork_url;

  // Build licensing object prioritizing explicit columns on the track row
  const licensingFromRow = supabaseTrackData.licensing || {};
  const licensingTierFromRow = supabaseTrackData.licensing_tier;

  const licensing = {
    ...licensingFromRow,
    // Prefer dedicated column when available so UI knows the tier immediately
    ...(licensingTierFromRow ? { tier: licensingTierFromRow } : {}),
  };

  const normalizeTerritories = (value: any): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === "string" ? item : String(item)))
        .filter(Boolean);
    }
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => (typeof item === "string" ? item : String(item)))
            .filter(Boolean);
        }
      } catch {
        return value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }
      return value ? [value] : [];
    }
    return [];
  };

  return {
    id: supabaseTrackData.id,
    user_id: supabaseTrackData.user_id || supabaseTrackData.artist_id, // Use user_id as the primary source

    title: supabaseTrackData.title || "Untitled",
    artist: artistName, // Use the actual artist from database
    cover_art_url: signedCoverArtUrl, // Try both fields
    uploader_name: uploaderName, // Name of the user who uploaded the draft
    original_filename: supabaseTrackData.original_filename, // Original filename before recognition

    // Version family — passed through so the drafts list can collapse a main
    // work and its stems/instrumentals/edits into one row.
    parent_track_id: supabaseTrackData.parent_track_id ?? null,
    version_type: supabaseTrackData.version_type ?? null,
    edit_type: supabaseTrackData.edit_type ?? null,

    metadata: metadata,

    rights: supabaseTrackData.rights,
    lyrics: supabaseTrackData.lyrics || { content: "" },
    tags: supabaseTrackData.tags,

    status: supabaseTrackData.status || {
      phase: "draft",
      clearance: false,
      monetization: false,
      public: false,
      flags: [],
    },
    is_published: supabaseTrackData.is_published ?? false,
    analysis_status: supabaseTrackData.analysis_status || "pending",
    error_message: supabaseTrackData.error_message,

    // Metadata source tracking (ACR vs embedded tags comparison)
    metadata_source: supabaseTrackData.metadata_source,
    needs_metadata_review: supabaseTrackData.needs_metadata_review ?? false,
    embedded_metadata: supabaseTrackData.embedded_metadata,

    mixes: supabaseTrackData.mixes,
    licensing,
    contact: supabaseTrackData.contact || null,
    grouping: supabaseTrackData.grouping || null,
    c_line: supabaseTrackData.c_line || null,
    p_line: supabaseTrackData.p_line || null,
    sync_context: supabaseTrackData.sync_context || null,
    rights_status: supabaseTrackData.rights_status || null,
    rights_needs_review: supabaseTrackData.rights_needs_review ?? null,
    mlc_song_code: supabaseTrackData.mlc_song_code ?? null,
    territories_included: normalizeTerritories(
      supabaseTrackData.territories_included
    ),

    created_at: supabaseTrackData.created_at || new Date().toISOString(),
    updated_at: supabaseTrackData.updated_at,
    lastModified:
      supabaseTrackData.updated_at ||
      supabaseTrackData.created_at ||
      new Date().toISOString(),
    progress: 0,
  };
};
